import React, { Fragment, useEffect, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { i18nMark } from "@lingui/react";
import { t } from "@lingui/macro";

import { i18n } from "base/i18n";
import { useStore } from "base/basestore";
import { Pagination } from "kfcomponents/pagination";
import { ReadOnlyGrid } from "kfcomponents/readonlygrid";
import { Progress, Tooltip, TOOLTIP_POSITION } from "widgets/feedback";
import { Scrollbar } from "widgets/scrollbar";
import {
  UserIcon,
  AVATAR_SIZE,
  StatusChip,
  STATUS_CHIP_TYPE,
  Icon
} from "widgets/datadisplay";
import { hasFeatures } from "widgets/others";
import { ErrorBoundary } from "widgets/layouts";

import { useProcessStore } from "../../common/stores/process";
import { STATUS_LABELS, PROCESS_FEATURES } from "../../common/constant";
import { STATUS_COLOR } from "../../constants";

import styles from "./grid.css";

import ExclamatoryIcon from "../icons/ExclamatoryIcon.svg";

const noop = () => {};

export const GRID_EVENTS = {
  PageChange: "PageChange",
  PageSizeChange: "PageSizeChange",
  ColumnReorder: "ColumnReorder",
  ColumnResize: "ColumnResize"
};
export const COLORS_LIST = {
  InProgress: "var(--secondary-orange-500)",
  Completed: "var(--secondary-green-500)"
};
export const HEADER_MENU_LIST = [
  { content: i18nMark("Sort in ascending order"), key: "ASC" },
  { content: i18nMark("Sort in descending order"), key: "DESC" },
  { content: i18nMark("Hide column"), key: "Hide" }
];
export const SORTING_HEADER_MENU_LIST = HEADER_MENU_LIST.slice(0, 2);

export const GetIndexCell = (
  onRowSelect,
  selectedRowsIds,
  gridPageNumber,
  gridPageSize,
  onErrorHover
) => {
  return {
    type: "checkbox",
    columnTitle: undefined,
    selectedRowKeys: selectedRowsIds,
    onChange: onRowSelect ? onRowSelect : undefined,
    columnWidth: "60px",
    fixed: true,
    renderCell: (value, record, index, originNode) => {
      return (
        <>
          {record.showCheckbox && (
            <React.Fragment>
              <span className={"selection-index"}>
                {(gridPageNumber - 1) * gridPageSize + index + 1}
              </span>
              <span className={"selection-check"}>{originNode}</span>{" "}
            </React.Fragment>
          )}
          {record.showSpinner && (
            <div>
              <div className={styles.spinner} />
            </div>
          )}
          {record.showError && (
            <Tooltip
              position={TOOLTIP_POSITION.LEFT}
              onVisibleChange={(event) => {
                if (event) {
                  onErrorHover(record);
                }
              }}
              content={
                record.errorMessage
                  ? record.errorMessage
                  : i18n._(
                      t`This item is not approved as it requires your inputs`
                    )
              }
            >
              <Icon paddingNone={true}>
                <ExclamatoryIcon />
              </Icon>
            </Tooltip>
          )}
        </>
      );
    }
  };
};

export function Grid({
  onPreferenceUpdate,
  getGriData,
  onRowClick,
  showRowSelection,
  headerMenuList,
  rowSelection,
  rowClickable,
  key,
  flowSchema
}) {
  const { useStateStore: useGridStore } = useStore();

  const gridData = useGridStore((state) => state.gridData);
  const gridCount = useGridStore((state) => state.gridCount);
  const gridPageNumber = useGridStore((state) => state.gridPageNumber);
  const showFilter = useGridStore((state) => state.showFilter);
  const gridPageSize = useGridStore((state) => state.gridPageSize);
  const gridLoading = useGridStore((state) => state.gridLoading);
  const selectedRowsIds = useGridStore((state) => state.selectedRowsIds);
  const { flowModel } = useProcessStore.getState();

  Grid.defaultProps = {
    rowClickable: true
  };

  const configJson = useMemo(
    function getconfigJson() {
      return {
        Columns: gridData.Columns,
        Filter: gridData.Filter,
        FilterType: gridData.FilterType,
        PageSize: gridData.PageSize,
        Sort: gridData.Sort
      };
    },
    [gridData]
  );

  useEffect(
    function checkIsGridEmptied() {
      const { gridCount } = useGridStore.getState();
      if (key !== "mytasks" && gridData.Data?.length === 0 && gridCount > 0) {
        changePageNo(Math.max(gridPageNumber - 1, 1));
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gridData]
  );

  const getColumnsWithMenuList = useCallback(
    function getUpdatedColumn(oldColumns) {
      if (oldColumns && !oldColumns[0]?.headerMenuList) {
        const { gridData } = useGridStore.getState();
        oldColumns = gridData.Columns.map((column) => {
          if (!column.hideSort) {
            let updatedColumn = { ...column };
            updatedColumn["headerMenuList"] =
              headerMenuList || HEADER_MENU_LIST;
            if (updatedColumn.Id === "_progress") {
              updatedColumn["headerMenuList"] = SORTING_HEADER_MENU_LIST;
            }
            if (gridData?.Sort[0]?.Id === column.Id) {
              updatedColumn["activeHeaderMenuKey"] = gridData.Sort[0].SortType;
            }
            return updatedColumn;
          }
          column.showHeaderOptions = false;
          return column;
        });
      }
      return oldColumns;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [headerMenuList]
  );
  const removeMenuListFromColumns = (columns) => {
    let newColumns = columns.map((column) => {
      let newColumn = { ...column, headerMenuList: undefined };
      return newColumn;
    });
    return newColumns;
  };
  const moveColumn = useCallback(
    function onMoveColumn(fromIndex, toIndex) {
      const { gridData } = useGridStore.getState();
      let newConfigJson = { ...configJson };
      let newCol = [...gridData.Columns];
      if (toIndex >= newCol.length) {
        var k = toIndex - newCol.length + 1;
        while (k--) {
          newCol.push(undefined);
        }
      }
      newCol.splice(toIndex, 0, newCol.splice(fromIndex, 1)[0]);
      newConfigJson.Columns = newCol;
      useGridStore
        .getState()
        .setGridData({ ...gridData, Columns: newConfigJson.Columns });
      newConfigJson.Columns = removeMenuListFromColumns(newConfigJson.Columns);
      onPreferenceUpdate({
        config: newConfigJson,
        type: GRID_EVENTS.ColumnReorder,
        dontCallGetApi: true
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configJson]
  );

  const resizeColumn = useCallback(
    function onResizeColumn(columnId, size) {
      const { gridData } = useGridStore.getState();
      let newConfigJson = { ...configJson };
      newConfigJson.Columns = gridData.Columns.map((column) => {
        if (column.Id === columnId) {
          column.Width = size.width;
          return column;
        } else {
          return column;
        }
      });
      useGridStore
        .getState()
        .setGridData({ ...gridData, Columns: newConfigJson.Columns });
      newConfigJson.Columns = removeMenuListFromColumns(newConfigJson.Columns);
      onPreferenceUpdate({
        config: newConfigJson,
        dontCallGetApi: true,
        type: GRID_EVENTS.ColumnResize
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configJson]
  );

  const changePageNo = (pageIndex, pageSize) => {
    if (pageSize && pageSize !== gridPageSize) {
      useGridStore.getState().setGridLoading(true);
      changePageSize(gridPageSize, pageSize);
    } else if (pageIndex && pageIndex !== gridPageNumber) {
      useGridStore.getState().setGridPageNumber(pageIndex);
      useGridStore.getState().setGridLoading(true);
      getGriData(pageIndex);
    }
  };

  const changePageSize = (oldSize, newSize) => {
    useGridStore.getState().setGridPageSize(newSize);
    // useGridStore.getState().setGridPageNumber(1);
    let newConfigJson = { ...configJson, PageSize: newSize };
    newConfigJson.Columns = removeMenuListFromColumns(newConfigJson.Columns);
    onPreferenceUpdate({
      config: newConfigJson,
      pageNumber: 1,
      dontCallGetApi: false,
      type: GRID_EVENTS.PageSizeChange
    });
  };

  const onHeaderOptionChange = useCallback(
    function headerOptionChange(optionType, columnId) {
      const { gridData } = useGridStore.getState();
      let newConfigJson = { ...configJson };
      if (optionType === "ASC" || optionType === "DESC") {
        newConfigJson.Sort = [{ SortType: optionType, Id: columnId }];
        newConfigJson.Columns = newConfigJson.Columns.map((column) => {
          if (column.Id === columnId) {
            column["activeHeaderMenuKey"] = optionType;
          } else if (column.activeHeaderMenuKey) {
            Reflect.deleteProperty(column, "activeHeaderMenuKey");
          }
          return column;
        });
      } else {
        newConfigJson.Columns = gridData.Columns.filter(
          (column) => column.Id !== columnId
        );
      }
      useGridStore.getState().setGridLoading(true);
      newConfigJson.Columns = removeMenuListFromColumns(newConfigJson.Columns);
      onPreferenceUpdate({ config: newConfigJson, type: optionType });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [configJson]
  );

  const customCells = {
    Name: (value) => <span className={"base600"}>{value}</span>,
    AssignedTo: (value) => {
      return (
        <div className={styles.item}>
          <UserIcon
            className={styles.itemProfileImage}
            userId={value[0]._id}
            name={value[0].Name}
            size={AVATAR_SIZE.SIZE36}
          />
          <div>
            <div className={`base`}>{value[0].Name}</div>
          </div>
        </div>
      );
    },
    _status: (value, func, row, column) => {
      return (
        <div className={styles.chipWidth}>
          <StatusChip color={STATUS_COLOR[value]} type={STATUS_CHIP_TYPE.LITE}>
            {STATUS_LABELS[value]?.label}
          </StatusChip>
        </div>
      );
    },
    _progress: (value) => {
      return (
        <Progress
          className={styles.progressBorderRadius}
          percent={value}
          strokeWidth={8}
          strokeColor={
            value < 100 ? COLORS_LIST.InProgress : COLORS_LIST.Completed
          }
          trailColor={"var(--gray-300)"}
        />
      );
    }
  };
  const onRowSelect = (rows) => {
    if (!rows.length) {
      useGridStore.getState().setSelectedRowsIds([]);
    } else {
      useGridStore.getState().setSelectedRowsIds(rows);
    }
  };
  return (
    <ErrorBoundary>
      <div
        className={`${styles.listContainer} ${
          showFilter && styles.filterContained
        }`}
      >
        {gridData.Columns && (
          <Fragment>
            <Scrollbar scrollX={true} className={styles.grid}>
              <ReadOnlyGrid
                onHeaderOption={onHeaderOptionChange}
                columns={getColumnsWithMenuList(gridData.Columns)}
                showHeaderOptions={true}
                showRowSelection={showRowSelection}
                dataSource={gridData.Data}
                aggregation={gridData.Aggregation}
                onColumnResizeStop={resizeColumn}
                onColumnDrop={moveColumn}
                showSerialNo={true}
                currentPageNo={gridPageNumber}
                pageSize={gridPageSize}
                loading={gridLoading}
                onClick={onRowClick}
                onRowSelect={onRowSelect}
                rowClickable={rowClickable}
                customCells={customCells}
                selectedRows={selectedRowsIds}
                headerMenuList={headerMenuList || HEADER_MENU_LIST}
                schema={flowSchema || flowModel}
                fixedWidth={true}
                customRowSelection={
                  hasFeatures([PROCESS_FEATURES.ALLOW_BULK_ACTIONS]) &&
                  rowSelection
                }
              />
            </Scrollbar>
            <Pagination
              total={gridCount}
              onShowSizeChange={noop}
              onChange={changePageNo}
              pageSize={gridPageSize}
              current={gridPageNumber}
            />
          </Fragment>
        )}
      </div>
    </ErrorBoundary>
  );
}

Grid.propTypes = {
  onPreferenceUpdate: PropTypes.func,
  getGriData: PropTypes.func,
  onRowClick: PropTypes.func,
  showRowSelection: PropTypes.bool,
  headerMenuList: PropTypes.array,
  rowSelection: PropTypes.object,
  rowClickable: PropTypes.bool,
  key: PropTypes.string,
  flowSchema: PropTypes.object
};
